import DashboardLayout from "@/components/layouts/DashboardLayout";
import { useUserDetails } from "@/hooks/api/useUserDetails";
import { useSuggestionsInfinite } from "@/hooks/api/useSuggestions";
import { useState, useMemo, useEffect } from "react";
import { User, Calendar, CheckCircle2, XCircle, Clock, Lightbulb, Link2, ChevronDown, ChevronUp, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { Suggestion, StatusEnum } from "@/types/api";
import { suggestionsApi, type Recommendation, type Followup } from "@/api/suggestions";
import { Skeleton } from "@/components/ui/skeleton";
import { SuggestionCardSkeleton } from "@/components/skeletons/SuggestionCardSkeleton";
import { useAnalytics } from "@/hooks/useAnalytics";

const Suggestions = () => {
  const navigate = useNavigate();
  const { data: userDetails } = useUserDetails();
  const { trackUserInteraction, trackButtonClick } = useAnalytics();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sortOrder, setSortOrder] = useState<string>("-created_at");

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading
  } = useSuggestionsInfinite({
    ordering: sortOrder,
  });

  const firstName = userDetails?.first_name || "there";
  const allSuggestions = data?.pages.flatMap(page => page.results) || [];
  const [cardData, setCardData] = useState<Record<number, { recommendations: Recommendation[], followups: Followup[], loading: boolean }>>({});

  // Fetch recommendations and followups for all suggestions
  useEffect(() => {
    const fetchAllDetails = async () => {
      if (!allSuggestions.length) return;

      const fetchPromises = allSuggestions.map(async (suggestion) => {
        if (cardData[suggestion.id]) return; // Skip if already fetched

        try {
          const [recommendations, followups] = await Promise.all([
            suggestionsApi.getRecommendations(suggestion.id),
            suggestionsApi.getFollowups(suggestion.id)
          ]);

          setCardData(prev => ({
            ...prev,
            [suggestion.id]: { recommendations, followups, loading: false }
          }));
        } catch (error) {
          console.error(`Error fetching details for suggestion ${suggestion.id}:`, error);
          setCardData(prev => ({
            ...prev,
            [suggestion.id]: { recommendations: [], followups: [], loading: false }
          }));
        }
      });

      await Promise.all(fetchPromises);
    };

    fetchAllDetails();
  }, [allSuggestions]);

  // Filter suggestions by status
  const filteredSuggestions = useMemo(() => {
    if (statusFilter === "all") return allSuggestions;
    return allSuggestions.filter(s => s.status === statusFilter);
  }, [allSuggestions, statusFilter]);

  // Calculate stats
  const stats = useMemo(() => {
    const total = allSuggestions.length;
    const pending = allSuggestions.filter(s => s.status === "pending").length;

    // Calculate completed this week
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);

    const completedThisWeek = allSuggestions.filter(s => {
      if (s.status !== "completed" || !s.completed_at) return false;
      const completedDate = new Date(s.completed_at);
      return completedDate >= startOfWeek;
    }).length;

    return { total, pending, completedThisWeek };
  }, [allSuggestions]);

  const getPriorityColor = (priority: number) => {
    switch (priority) {
      case 3: return "text-red-600 bg-red-50";
      case 2: return "text-amber-600 bg-amber-50";
      case 1: return "text-green-600 bg-green-50";
      default: return "text-gray-600 bg-gray-50";
    }
  };

  const getPriorityLabel = (priority: number) => {
    switch (priority) {
      case 3: return "High";
      case 2: return "Medium";
      case 1: return "Low";
      default: return "Unknown";
    }
  };

  const getStatusBadge = (status: StatusEnum) => {
    switch (status) {
      case "pending":
        return <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-yellow-50 text-yellow-700">
          <Clock className="w-3 h-3" /> Pending
        </span>;
      case "completed":
        return <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-50 text-green-700">
          <CheckCircle2 className="w-3 h-3" /> Completed
        </span>;
      case "dismissed":
        return <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-gray-50 text-gray-700">
          <XCircle className="w-3 h-3" /> Dismissed
        </span>;
      case "scheduled":
        return <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700">
          <Calendar className="w-3 h-3" /> Scheduled
        </span>;
      case "expired":
        return <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-red-50 text-red-700">
          <XCircle className="w-3 h-3" /> Expired
        </span>;
      default:
        return null;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <DashboardLayout>
      <div className="container mx-auto px-4 md:px-8">
        <div className="py-6 md:py-8">
          {/* Header Section */}
          <div className="mb-8">
            <h2 className="text-2xl font-medium font-euclid text-wabisabi-plum mb-2">
              Hi {firstName}, here are your personalized suggestions
            </h2>
            <p className="text-gray-600">AI-powered insights to help you nurture your relationships.</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-3 md:gap-6 mb-8">
            {/* Total Suggestions Card */}
            <div className="bg-white rounded-lg p-3 md:p-6 text-center">
              <h3 className="text-[12px] md:text-[14px] font-medium text-black mb-4 md:mb-[35px]">Total Suggestions</h3>
              <p className="text-[24px] md:text-[32px] font-bold text-black">{stats.total}</p>
            </div>

            {/* Pending Suggestions Card */}
            <div className="bg-white rounded-lg p-3 md:p-6 text-center">
              <h3 className="text-[12px] md:text-[14px] font-medium text-black mb-4 md:mb-[35px]">Pending</h3>
              <p className="text-[24px] md:text-[32px] font-bold text-black">{stats.pending}</p>
            </div>

            {/* Completed This Week Card */}
            <div className="bg-white rounded-lg p-3 md:p-6 text-center">
              <h3 className="text-[12px] md:text-[14px] font-medium text-black mb-4 md:mb-[35px]">Completed This Week</h3>
              <p className="text-[24px] md:text-[32px] font-bold text-black">{stats.completedThisWeek}</p>
            </div>
          </div>

          {/* Filters Section */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row gap-4 items-center">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Filter by Status</label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[200px] h-9">
                    <SelectValue placeholder="All Suggestions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Suggestions</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="dismissed">Dismissed</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Sort By</label>
                <Select value={sortOrder} onValueChange={setSortOrder}>
                  <SelectTrigger className="w-[200px] h-9">
                    <SelectValue placeholder="Newest First" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="-created_at">Newest First</SelectItem>
                    <SelectItem value="created_at">Oldest First</SelectItem>
                    <SelectItem value="-priority">Priority (High to Low)</SelectItem>
                    <SelectItem value="priority">Priority (Low to High)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Suggestions Section */}
          <div className="mb-8">
            <h3 className="text-[20px] font-bold font-euclid text-wabisabi-plum mb-4">
              {statusFilter === "all" ? "All Suggestions" : `${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)} Suggestions`}
            </h3>

            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <SuggestionCardSkeleton />
                <SuggestionCardSkeleton />
                <SuggestionCardSkeleton />
                <SuggestionCardSkeleton />
                <SuggestionCardSkeleton />
                <SuggestionCardSkeleton />
              </div>
            ) : filteredSuggestions.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredSuggestions.map((suggestion) => {
                  const data = cardData[suggestion.id];
                  const hasRecommendations = data && data.recommendations.length > 0;
                  const hasFollowups = data && data.followups.length > 0;
                  const showBadges = hasRecommendations || hasFollowups;

                  return (
                  <div
                    key={suggestion.id}
                    className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col cursor-pointer"
                    onClick={() => navigate(`/suggestions/${suggestion.id}`)}
                  >
                    {/* Header with Avatar and Status */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-12 h-12">
                          {suggestion.contact_avatar ? (
                            <AvatarImage
                              src={`/images/avatars/${suggestion.contact_avatar}`}
                              alt={suggestion.contact_name}
                            />
                          ) : null}
                          <AvatarFallback className="bg-gray-200">
                            <User className="w-6 h-6 text-gray-500" />
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{suggestion.contact_name}</p>
                          <p className="text-xs text-gray-500 capitalize">{suggestion.suggestion_type}</p>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 items-end">
                        {getStatusBadge(suggestion.status)}
                      </div>
                    </div>

                    {/* Title */}
                    <h4 className="text-[16px] font-bold text-black mb-2">
                      {suggestion.title}
                    </h4>

                    {/* Content */}
                    <p className="text-[14px] text-gray-600 mb-4 flex-grow line-clamp-2">
                      {suggestion.content}
                    </p>

                    {/* Info Section */}
                    <div className="mb-4 space-y-2">
                      {data && (hasRecommendations || hasFollowups) ? (
                        <div className="text-sm text-gray-700 space-y-1">
                          {hasRecommendations && (
                            <div className="flex items-center gap-2">
                              <Lightbulb className="w-4 h-4 text-blue-600" />
                              <span>{data.recommendations.length} recommendation{data.recommendations.length !== 1 ? 's' : ''}</span>
                            </div>
                          )}
                          {hasFollowups && (
                            <div className="flex items-center gap-2">
                              <Link2 className="w-4 h-4 text-purple-600" />
                              <span>{data.followups.length} nudge{data.followups.length !== 1 ? 's' : ''}</span>
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>

                    {/* Footer with Priority and Date */}
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span className={`px-2 py-1 rounded-full font-medium ${getPriorityColor(suggestion.priority)}`}>
                        {getPriorityLabel(suggestion.priority)} Priority
                      </span>
                      <span>{formatDate(suggestion.created_at)}</span>
                    </div>
                  </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white rounded-lg p-12 text-center">
                <p className="text-gray-500">
                  {statusFilter === "all"
                    ? "No suggestions available yet. Keep adding memories and we'll generate personalized suggestions for you!"
                    : `No ${statusFilter} suggestions found.`}
                </p>
              </div>
            )}

            {/* Load More Button */}
            {filteredSuggestions.length > 0 && hasNextPage && (
              <div className="flex justify-center mt-6">
                <Button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  variant="outline"
                  className="min-w-[150px]"
                >
                  {isFetchingNextPage ? (
                    <Skeleton className="h-4 w-20" />
                  ) : (
                    "Load More"
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Suggestions;
